import { useContext } from "react";
import {
  Disclosure,
  DisclosurePanel,
  DisclosureStateContext,
  Heading,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

import Button from "./Button";

function AccordionItemContent({
  children,
  unmountOnCollapse,
}: {
  readonly children: React.ReactNode;
  readonly unmountOnCollapse: boolean;
}) {
  const disclosureState = useContext(DisclosureStateContext);
  if (unmountOnCollapse && disclosureState?.isExpanded === false) return null;

  return <div className="py-4">{children}</div>;
}

type AccordionItemProps = {
  children: React.ReactNode;
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  indicator?: {
    icon?: React.ReactNode;
    label?: string;
  };
  className?: string;
  unmountOnCollapse?: boolean;
};

export default function AccordionItem({
  children,
  id,
  title,
  subtitle,
  indicator,
  className,
  unmountOnCollapse = false,
}: Readonly<AccordionItemProps>) {
  return (
    <Disclosure
      id={id}
      className={twMerge(
        `group/disclosure border border-transparent transition-colors duration-100`,
        className,
      )}
    >
      <Heading>
        <Button
          slot="trigger"
          className={`text-text-primary w-full justify-between pr-2 pl-0 text-base`}
        >
          <div className="flex flex-col">
            {title}
            {subtitle && (
              <span className="text-text-secondary">{subtitle}</span>
            )}
          </div>
          <div className="text-text-secondary flex items-center gap-2">
            {indicator?.label}
            <svg
              aria-hidden="true"
              width="14"
              height="9"
              viewBox="0 0 14 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={`transition-transform duration-300 group-data-[expanded=true]/disclosure:rotate-180`}
            >
              <path
                d="M1 1L7 7L13 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </Button>
      </Heading>
      <DisclosurePanel>
        <AccordionItemContent unmountOnCollapse={unmountOnCollapse}>
          {children}
        </AccordionItemContent>
      </DisclosurePanel>
    </Disclosure>
  );
}
